import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Button,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Paper,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import {
  CalendarToday,
  AccessTime,
  LocationOn,
  Schedule,
  Refresh,
  EventAvailable,
  SentimentDissatisfied,
  CheckCircle,
  Cancel,
  Block,
  DoneAll,
  Payment,
  Notes,
  Groups,
} from '@mui/icons-material';
import axios from '../config/axios';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import ModalConfirm from '../components/ModalConfirm';
import ModalResult from '../components/ModalResult';
import { canMemberCancelReservation } from '../utils/reservationCancellation';
import {
  getCategoryLabel,
  getCategoryColor,
  formatDuration,
} from '../utils/reservableHelpers';

const formatReservationDay = (date) => {
  const d = dayjs(date).locale('it');
  const dayName = d.format('dddd');
  const capitalized = dayName.charAt(0).toUpperCase() + dayName.slice(1);
  return {
    dayName: capitalized,
    full: `${capitalized} ${d.format('D MMMM YYYY')}`,
    short: `${capitalized} ${d.format('D MMM YYYY')}`,
  };
};

const STATUS_CONFIG = {
  pending: { label: 'In attesa', color: 'warning', icon: Schedule },
  approved: { label: 'Approvata', color: 'success', icon: CheckCircle },
  rejected: { label: 'Rifiutata', color: 'error', icon: Cancel },
  cancelled: { label: 'Annullata', color: 'default', icon: Block },
  completed: { label: 'Completata', color: 'info', icon: DoneAll },
};

const InfoItem = ({ icon: Icon, label, value }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 1.5,
      p: 1.5,
      borderRadius: 2,
      bgcolor: (theme) => alpha(theme.palette.primary.main, 0.04),
      border: '1px solid',
      borderColor: 'divider',
      height: '100%',
    }}
  >
    <Icon sx={{ fontSize: 20, color: 'primary.main', mt: 0.25 }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" display="block" lineHeight={1.2}>
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-word' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

const MyReservations = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [resultOpen, setResultOpen] = useState(false);
  const [resultTitle, setResultTitle] = useState('');
  const [resultMessage, setResultMessage] = useState('');
  const [resultSeverity, setResultSeverity] = useState('info');
  const [reservationToCancel, setReservationToCancel] = useState(null);

  useEffect(() => {
    fetchReservations();
  }, []);

  const fetchReservations = async () => {
    try {
      setLoading(true);
      setError(null);
      const today = dayjs().format('YYYY-MM-DD');
      const response = await axios.get(`/api/reservations?status=active&dateFrom=${today}`);
      setReservations(response.data);
    } catch (err) {
      setError('Errore nel caricamento delle prenotazioni');
      console.error('Error fetching reservations:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelClick = (reservationId) => {
    setReservationToCancel(reservationId);
    setConfirmOpen(true);
  };

  const handleConfirmCancel = async () => {
    setConfirmOpen(false);

    try {
      await axios.put(`/api/reservations/${reservationToCancel}/cancel`, {
        reason: 'Annullata dall\'utente',
      });
      await fetchReservations();

      setResultTitle('Prenotazione Annullata');
      setResultMessage('La prenotazione è stata annullata con successo.');
      setResultSeverity('success');
      setResultOpen(true);
    } catch (err) {
      console.error('Error cancelling reservation:', err);
      setResultTitle('Errore');
      setResultMessage(
        err.response?.data?.message ||
          'Si è verificato un errore durante l\'annullamento della prenotazione. Riprova più tardi.'
      );
      setResultSeverity('error');
      setResultOpen(true);
    }

    setReservationToCancel(null);
  };

  const handleCancelConfirm = () => {
    setConfirmOpen(false);
    setReservationToCancel(null);
  };

  const handleResultClose = () => {
    setResultOpen(false);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: 320,
            gap: 2,
          }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Caricamento prenotazioni...
          </Typography>
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" startIcon={<Refresh />} onClick={fetchReservations}>
          Riprova
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: { xs: 'flex-start', sm: 'center' },
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EventAvailable color="primary" sx={{ fontSize: 36 }} />
          <Box>
            <Typography variant="h5" fontWeight={700} color="primary.main">
              Le Mie Prenotazioni
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Prenotazioni attive e future
            </Typography>
          </Box>
        </Box>

        {reservations.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={`${reservations.length} ${reservations.length === 1 ? 'prenotazione' : 'prenotazioni'}`}
              color="primary"
              variant="outlined"
              size="small"
              sx={{ fontWeight: 600 }}
            />
            <Tooltip title="Aggiorna elenco">
              <IconButton onClick={fetchReservations} size="small" color="primary">
                <Refresh />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {reservations.length === 0 ? (
        <Card
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            boxShadow: 2,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <SentimentDissatisfied color="disabled" sx={{ fontSize: 48, mb: 1.5 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Nessuna prenotazione attiva
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 360 }}>
            Non hai prenotazioni in programma. Sfoglia le risorse disponibili per prenotare sala, attrezzatura o lezioni.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/reservables')}>
            Vai alle Prenotazioni
          </Button>
        </Card>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {reservations.map((reservation) => {
            const statusCfg = STATUS_CONFIG[reservation.status] || {
              label: reservation.status,
              color: 'default',
              icon: Schedule,
            };
            const StatusIcon = statusCfg.icon;
            const showPaymentNote = reservation.paymentStatus === 'pending';
            const reservationDay = formatReservationDay(reservation.date);
            const accessCount = reservation.accessCount ?? 1;

            return (
              <Card
                key={reservation._id}
                sx={{
                  boxShadow: 2,
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    px: 2.5,
                    py: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    flexWrap: 'wrap',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CalendarToday sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Typography variant="subtitle1" fontWeight={700} color="primary.main">
                      {reservationDay.short}
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    fontWeight={700}
                    color="primary.main"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {reservation.totalPrice}€
                  </Typography>
                </Box>

                <CardContent sx={{ p: { xs: 2, sm: 2.5 }, '&:last-child': { pb: { xs: 2, sm: 2.5 } } }}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 1.5,
                      mb: 1.5,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={700}
                        color="text.primary"
                        sx={{ fontSize: 18, mb: 0.75 }}
                      >
                        {reservation.reservable?.name || 'Risorsa non disponibile'}
                      </Typography>
                      {reservation.reservable?.category && (
                        <Chip
                          label={getCategoryLabel(reservation.reservable.category)}
                          color={getCategoryColor(reservation.reservable.category)}
                          size="small"
                          sx={{ fontWeight: 600 }}
                        />
                      )}
                    </Box>
                    <Chip
                      label={statusCfg.label}
                      color={statusCfg.color}
                      size="small"
                      icon={<StatusIcon sx={{ fontSize: '16px !important' }} />}
                      sx={{ fontWeight: 600, height: 28 }}
                    />
                  </Box>

                  {showPaymentNote && (
                    <Alert
                      severity="warning"
                      icon={<Payment />}
                      sx={{
                        mb: 2,
                        borderRadius: 2,
                        fontWeight: 600,
                        '& .MuiAlert-message': { fontSize: '0.95rem' },
                      }}
                    >
                      Il pagamento deve essere effettuato presso la segreteria
                    </Alert>
                  )}

                  <Divider sx={{ mb: 2 }} />

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                      gap: 1.5,
                      mb: reservation.notes || reservation.adminNotes ? 2 : 0,
                    }}
                  >
                    <InfoItem
                      icon={CalendarToday}
                      label="Giorno"
                      value={reservationDay.full}
                    />
                    <InfoItem
                      icon={AccessTime}
                      label="Orario"
                      value={`${reservation.startTime} – ${reservation.endTime}`}
                    />
                    <InfoItem
                      icon={Schedule}
                      label="Durata"
                      value={formatDuration(reservation.duration)}
                    />
                    <InfoItem
                      icon={LocationOn}
                      label="Luogo"
                      value={reservation.reservable?.location || 'Non specificato'}
                    />
                    {accessCount > 1 && (
                      <InfoItem icon={Groups} label="Accessi" value={`${accessCount} posti`} />
                    )}
                  </Box>

                  {reservation.notes && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        mb: reservation.adminNotes ? 1.5 : 0,
                        borderRadius: 2,
                        bgcolor: 'background.default',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
                        <Notes sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="caption" fontWeight={600} color="text.secondary">
                          Le tue note
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {reservation.notes}
                      </Typography>
                    </Paper>
                  )}

                  {reservation.adminNotes && (
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: (theme) => alpha(theme.palette.info.main, 0.04),
                        borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
                      }}
                    >
                      <Typography variant="caption" fontWeight={600} color="info.main" display="block" sx={{ mb: 0.5 }}>
                        Nota dall&apos;amministrazione
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {reservation.adminNotes}
                      </Typography>
                    </Paper>
                  )}

                  <Divider sx={{ my: 2 }} />

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: { xs: 'flex-start', sm: 'center' },
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 1.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Richiesta del {dayjs(reservation.createdAt).locale('it').format('D MMM YYYY, HH:mm')}
                    </Typography>

                    {canMemberCancelReservation(reservation) && (
                      <Button
                        variant="outlined"
                        color="error"
                        size="small"
                        onClick={() => handleCancelClick(reservation._id)}
                        sx={{ fontWeight: 600, borderRadius: 2, alignSelf: { xs: 'stretch', sm: 'auto' } }}
                      >
                        Annulla prenotazione
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            );
          })}
        </Box>
      )}

      <ModalConfirm
        open={confirmOpen}
        title="Conferma Annullamento"
        message="Sei sicuro di voler annullare questa prenotazione? Questa azione non può essere annullata."
        onConfirm={handleConfirmCancel}
        onCancel={handleCancelConfirm}
      />

      <ModalResult
        open={resultOpen}
        onClose={handleResultClose}
        title={resultTitle}
        message={resultMessage}
        severity={resultSeverity}
      />
    </Container>
  );
};

export default MyReservations;
